/**
 * Locale decimal/percentage formatting — replaces numeral.js in UtilFormat.
 *
 * Behavior locked by locale-decimal-format.test.js (characterization vs numeral).
 */

import { normalizeLocaleTag } from '../dates';

const DECIMAL_FORMAT_OPTIONS = {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
};

/**
 * Parse user input that may use comma as decimal separator.
 * @param {string|number} text
 * @returns {number}
 */
export const parseLocalizedDecimalInput = (text) => {
    if (typeof text === 'string') {
        return Number(text.replace(',', '.'));
    }
    return text;
};

/**
 * Format a value with exactly two fraction digits and locale decimal separator.
 * No thousands grouping (matches legacy numeral `0.00` pattern).
 *
 * @param {string|number} text
 * @param {string} locale — BCP 47 locale tag (e.g. "es-ES", "en-US")
 * @returns {string}
 */
export const getDecimalFormat = (text, locale) => {
    const parsed = parseLocalizedDecimalInput(text);
    const value = !isNaN(parseFloat(parsed)) ? parseFloat(Number(parsed).toFixed(2)) : 0;
    return new Intl.NumberFormat(
        normalizeLocaleTag(locale) || locale,
        DECIMAL_FORMAT_OPTIONS,
    ).format(value);
};

/**
 * Format a percentage for display outside inputs.
 * Legacy: only `es-ES` uses comma as decimal separator; other locales keep a dot.
 *
 * @param {string|number} text
 * @param {string} locale
 * @returns {string}
 */
export const getPercentageFormat = (text, locale) => {
    let parsed = text;
    if (typeof parsed === 'string') {
        parsed = !parsed ? 0 : Number(parsed.replace(',', '.'));
    }
    const value = !isNaN(parseFloat(parsed)) ? parseFloat(Number(parsed).toFixed(2)) : 0;
    let display = String(value);
    if (normalizeLocaleTag(locale) === 'es-ES') {
        display = display.replace('.', ',');
    }
    return `${display}%`;
};

/**
 * Parse a value to number; returns 0 for invalid input.
 * Legacy: does not normalize comma decimals (Number("12,5") → 0).
 *
 * @param {string|number} text
 * @returns {number}
 */
export const getNumberFormat = (text) => (!isNaN(Number(text)) ? Number(text) : 0);
